import { calcDeferral } from "./deferral";
import { calcLine } from "./line";
import { money, share } from "./policy";
import { buildRateIndex } from "./ratecard";
import type {
  Block,
  BlockCalc,
  CalcPolicy,
  Estimate,
  EstimateCalc,
  LineCalc,
  Ratecard,
  StageCalc,
  TaxScheme,
  Totals,
} from "./types";

const ZERO: Totals = { net: 0, vat: 0, gross: 0, vatIn: 0, cost: 0, profit: 0, margin: 0 };

/** Свёртка любого уровня дерева: строки → блок → этап → проект. */
function sum(parts: Totals[]): Totals {
  const t = parts.reduce(
    (acc, p) => ({
      net: acc.net + p.net,
      vat: acc.vat + p.vat,
      gross: acc.gross + p.gross,
      vatIn: acc.vatIn + p.vatIn,
      cost: acc.cost + p.cost,
      profit: acc.profit + p.profit,
      margin: 0,
    }),
    { ...ZERO },
  );
  return {
    net: money(t.net),
    vat: money(t.vat),
    gross: money(t.gross),
    vatIn: money(t.vatIn),
    cost: money(t.cost),
    profit: money(t.profit),
    margin: share(t.profit, t.net),
  };
}

export interface CalcContext {
  ratecard: Ratecard;
  scheme: TaxScheme;
  policy: CalcPolicy;
}

/**
 * Расчёт всей сметы.
 *
 * Отключённый блок (`enabled: false`) остаётся в дереве, но в итог не попадает —
 * по ТЗ исключённая опция должна быть видна и не должна влиять на сумму.
 */
export function calcEstimate(estimate: Estimate, ctx: CalcContext): EstimateCalc {
  const rateIndex = buildRateIndex(ctx.ratecard);
  const lineCtx = { rateIndex, scheme: ctx.scheme, policy: ctx.policy };

  const stages: StageCalc[] = estimate.stages.map((stage) => {
    const blocks: BlockCalc[] = stage.blocks.map((block) => {
      const lines: LineCalc[] = block.lines.map((l) => calcLine(l, lineCtx));
      const active = block.enabled === false ? [] : lines;
      const totals = sum(active);
      return {
        block,
        lines,
        ...totals,
        target: blockTarget(block.targetNet, totals.net, ctx.policy.blockTolerance),
        planMargin: planForBlock(block, estimate, ctx.policy),
      };
    });
    return { stage, blocks, ...sum(blocks) };
  });

  const base = sum(stages);
  // Схема без отсрочки (`hasDeferral: false`, ИП на УСН) отсрочку не считает,
  // даже если тумблер в смете остался включённым с прежней схемы: наценка
  // уезжала клиенту в файл, а налог с оборота считался с неё же.
  const deferral = ctx.scheme.hasDeferral
    ? calcDeferral(estimate.deferral, base)
    : { client: 0, own: 0 };

  const totalNet = money(base.net + deferral.client / (1 + ctx.scheme.vatRate));
  const totalGross = money(base.gross + deferral.client);
  const totalVat = money(totalGross - totalNet);

  const allBlocks = stages.flatMap((s) => s.blocks);

  /*
   * Отсрочка входит в экономику сметы обеими половинами.
   *
   * Клиенту выставляется наценка за рассрочку (`deferral.client`), а нам
   * замороженные до оплаты деньги чего-то стоят (`deferral.own`). Раньше в
   * прибыль не попадала ни та, ни другая: сумма для клиента росла, а
   * рентабельность не двигалась с места. Аня заметила это на демо — «даже
   * тут пока ничего не пересчитывается, да?».
   *
   * Тонкость, которая всплыла при проверке. Без аванса рентабельность и не
   * должна меняться: по формуле шаблона наценка клиенту равна `net × k`, а
   * стоимость денег — `cost × k`, то есть выручка и расход растут на один и
   * тот же множитель, и их отношение сохраняется. Отсрочка начинает двигать
   * рентабельность там, где есть аванс: наценка уменьшается вдвое при авансе
   * 50%, а стоимость наших денег падает до нуля, как только аванс покрывает
   * себестоимость. Тогда рассрочка становится заработком — и это видно.
   *
   * `net`, `gross` и суммы блоков остаются как были — по работам. Итоги с
   * отсрочкой лежат отдельно, в `totalNet` и `totalGross`.
   */
  const cost = money(base.cost + deferral.own);
  const profit = money(totalNet - cost);

  const result: EstimateCalc = {
    ...base,
    cost,
    profit,
    margin: share(profit, totalNet),
    stages,
    blocks: allBlocks,
    deferral,
    totalNet,
    totalVat,
    totalGross,
    // Сколько уйдёт в бюджет: наш НДС минус тот, что уже уплачен исполнителям.
    // Раньше входящий налог сидел в себестоимости и занижал рентабельность —
    // Аня это поправила: «НДС нужно учитывать к вычету».
    vatPayable: money(totalVat - base.vatIn),
    planMargin: blendedPlan(allBlocks) ?? planFor(estimate, ctx.policy),
  };

  // ИП на УСН: прибыль считается от выручки за вычетом налога с оборота.
  // Отсрочки у этой схемы не бывает (`hasDeferral: false` обнуляет её выше),
  // так что `totalNet` здесь равен выручке по работам.
  if (ctx.scheme.profitModel === "net") {
    const taxes = money(totalNet * (ctx.scheme.usnRate ?? 0));
    const netRevenue = money(totalNet - taxes);
    result.taxes = taxes;
    result.netRevenue = netRevenue;
    result.profit = money(netRevenue - cost);
    result.margin = share(result.profit, netRevenue);
  }

  return result;
}

/**
 * Выручка, от которой считается прибыль, — и с отсрочкой тоже.
 *
 * `net` — это сумма по работам, без наценки за отсрочку; итог с отсрочкой
 * лежит в `totalNet`, и прибыль считается именно от него. Проверки, бравшие
 * `net`, поэтому краснели на каждой смете с постоплатой: они сравнивали
 * величины из разных слоёв свода, и разница была ровно наценкой за отсрочку.
 *
 * Одна функция на все места, где эта величина нужна: контроли выпуска,
 * карточка контрольных нулей и та же карточка в выгрузке. Формула, записанная
 * трижды, разъезжается — она уже разъехалась.
 */
export function profitBase(calc: EstimateCalc): number {
  return calc.netRevenue ?? calc.totalNet;
}

/**
 * Себестоимость по строкам — без стоимости наших замороженных денег.
 *
 * Парная величина к `profitBase`: в `cost` сидит ещё и `deferral.own`,
 * которого ни в одном блоке нет.
 */
export function blocksCost(calc: EstimateCalc): number {
  return money(calc.blocks.reduce((s, b) => s + b.cost, 0) + calc.deferral.own);
}

/**
 * План проекта: своя цель сметы, потом подотдел, потом тип проекта.
 *
 * Своей цифры может не быть ни там, ни там: справочник заполняет агентство, и
 * пока Braining не назвали ни одной, работает общий ориентир из политики. Если
 * нет и его — плана нет вовсе, и проверка против плана просто не
 * показывается. Придуманный порог хуже отсутствующего.
 *
 * Цель сметы (`targetMargin`) стоит первой с 21.09. До этого поле «Целевая
 * рент.» на «Расчёте» писалось в смету, но расчёт его не читал: в шапке
 * по-прежнему стоял план подотдела, цвета считались от него же, и поле
 * выглядело сломанным. Жалоба Вики №3: «хочу менять целевую рентабельность».
 * Правило простое: чем ближе цифра к конкретной работе, тем она главнее —
 * блок важнее сметы, смета важнее подотдела.
 */
export function planFor(estimate: Estimate, policy: CalcPolicy): number | undefined {
  if (typeof estimate.targetMargin === "number") return estimate.targetMargin;

  const sub = estimate.subdivision;
  if (sub && typeof policy.planMargin.bySubdivision[sub] === "number") {
    return policy.planMargin.bySubdivision[sub];
  }
  return policy.planMargin.byType[estimate.projectType] ?? policy.planMarginDefault;
}

/**
 * План блока — то, с чем сравнивается его факт.
 *
 * Порядок от частного к общему: своя цель блока, цель всей сметы, план
 * подотдела блока, план проекта. Подотдел блока важнее типа проекта
 * намеренно — иначе в смешанной смете диджитал-блоки мерились бы брендовой
 * планкой, а Аня говорила ровно об обратном: «у них ещё и у самих бывает
 * разная целевая рентабельность — на уровне блока услуг».
 *
 * Цель сметы при этом важнее подотдела: её ставят руками на этот проект, а
 * подотдел — ориентир по умолчанию. Менеджер, поставивший смете 45 %, ждёт,
 * что и блоки меряются по 45, а не по плану своего направления.
 */
export function planForBlock(
  block: Block,
  estimate: Estimate,
  policy: CalcPolicy,
): number | undefined {
  if (typeof block.targetMargin === "number") return block.targetMargin;
  if (typeof estimate.targetMargin === "number") return estimate.targetMargin;

  const sub = block.subdivision ?? estimate.subdivision;
  if (sub && typeof policy.planMargin.bySubdivision[sub] === "number") {
    return policy.planMargin.bySubdivision[sub];
  }

  return policy.planMargin.byType[estimate.projectType] ?? policy.planMarginDefault;
}

/**
 * План сметы — средневзвешенный по включённым блокам.
 *
 * Когда блоки одного подотдела, совпадает с его планом. В смешанной смете
 * даёт честную цель вместо планки «главного» направления: полсметы диджитала
 * не обязаны выходить на брендовые цифры.
 */
function blendedPlan(blocks: BlockCalc[]): number | undefined {
  const active = blocks.filter(
    (b) => b.block.enabled !== false && b.net > 0 && typeof b.planMargin === "number",
  );
  const net = active.reduce((s, b) => s + b.net, 0);
  if (!net) return undefined;
  return active.reduce((s, b) => s + b.planMargin! * b.net, 0) / net;
}

function blockTarget(
  target: number | undefined,
  net: number,
  tolerance: number,
): BlockCalc["target"] {
  if (typeof target !== "number") return undefined;
  const diff = money(net - target);
  return {
    amount: target,
    diff,
    withinTolerance: Math.abs(diff) <= Math.abs(target) * tolerance,
  };
}
